export type CustomerDebtDetails = {
  notOverdueAmount: number;
  oneToThirtyDaysAmount: number;
  thirtyOneToSixtyDaysAmount: number;
  sixtyOneToNinetyDaysAmount: number;
  ninetyOneOrMoreDaysAmount: number;
};

export type CustomerDetails = CustomerDebtDetails & {
  intentionStatus: IntentionStatus;
  total: number;
  currency: string;
  lastSentEmailDate?: string | null;
};

export type IntentionStatusSummaryValues = {
  amount: number;
  count: number;
};

export type AutoEmailSendingMode = "AutoSendTemplatesOnly" | "Never";

export type UserRole = "ADMIN" | "TRIAL" | "USER";

export type OrgRole = "MANAGER" | "COLLECTOR" | "VIEWER";
export type MembershipStatus = "active" | "deactivated";

export type MembershipOrganization = {
  org_id: number;
  org_name: string;
  org_role: OrgRole | null;
  membership_status: MembershipStatus;
};

/** Route / feature access flags (JWT claim + DB `page_scopes`). */
export type PageScope =
  | "CASHFLOW"
  | "TRANSACTIONS"
  | "DASHBOARDS"
  | "RECEIVABLES"
  | "FILES_INTEGRATIONS"
  | "SETTINGS"
  | "ARCHIVE"
  | "ANALYTICS"
  | "ADMIN"
  | "FORECAST"
  | "SUPPLIER_INVOICES"
  | "BANK_RECONCILIATION";

export const ALL_PAGE_SCOPES: PageScope[] = [
  "DASHBOARDS",
  "CASHFLOW",
  "FORECAST",
  "TRANSACTIONS",
  "FILES_INTEGRATIONS",
  "RECEIVABLES",
  "SUPPLIER_INVOICES",
  "BANK_RECONCILIATION",
  "ARCHIVE",
  "ANALYTICS",
  "ADMIN",
  "SETTINGS",
];

export type MfaClaim = "not_required" | "pending" | "verified";

/** Organization-level feature flags (JWT `features` claim). */
export type OrgFeature =
  | "RECEIVABLES_PI_3RD_WIDGET"
  | "RECEIVABLES_MONTHLY_AGING_EXPORT"
  | "RECEIVABLES_PERCENT_CHART_VIEW"
  | "RECEIVABLES_GROUP_AGING_BREAKDOWN";

export type Organization = {
  name: string;
  accounting_software: string | null;
  /** True when Priority PDF credentials are stored (URL is never returned). */
  has_priority_url?: boolean;
  // Last time invoices were pulled from the accounting source (Settings → Refresh Invoices)
  last_accounting_data_download: string;
  // Last run timestamps for the scheduled email refresh cron (refresh_emails)
  last_e2e_refresh_start: string;
  last_e2e_refresh_end: string;
  quickbooks_authorization_url: string;
  xero_authorization_url: string;
  auto_email_sending_mode: AutoEmailSendingMode;
  mfa_enabled: boolean;
  mcp_enabled: boolean;
  dso_days_sales: number;
  dso_period_days: number;
};

export type OutgoingMailboxStatus = {
  uses_official_email: boolean;
  official_outgoing_email: string | null;
  mailbox_user_id: number | null;
  mailbox_email: string | null;
  mailbox_display_name: string | null;
  mailbox_connected: boolean;
  acting_user_is_mailbox_owner: boolean;
  from_header: string | null;
  issue_code: string | null;
  issue_message: string | null;
};

export type AdminOrganization = {
  id: number;
  name: string;
  official_outgoing_email: string | null;
  use_official_outgoing_email: boolean;
  support_email: string;
  accounting_software: string | null;
  priority_api_url: string | null;
  auto_email_sending_mode: AutoEmailSendingMode;
  mfa_enabled: boolean;
  mcp_enabled: boolean;
  outgoing_mailbox_setup_warning?: string | null;
};

export type AdminUser = {
  id: number;
  provider_user_id: string;
  username: string;
  email: string;
  budy_organization_id: number | null;
  budy_organization_ids?: number[];
  roles: UserRole;
  oauth_provider: string | null;
  page_scopes?: PageScope[];
  last_logged_in?: string | null;
  created_at?: string | null;
  has_uploaded_file?: boolean;
};

export type IntentionStatus =
  | "Approved"
  | "Unresolved"
  | "No_reply"
  | "Needs_attention"
  | "In_progress";

export type BucketOption = "next_7_days" | "next_30_days" | "next_year";

export type LineChartInvoice = {
  externalId: string;
  amount: number;
  currency: string;
};

export type LineChartDatum = {
  dateISO: string;
  due_date_label: string;
  invoicesByStatus: Record<IntentionStatus, LineChartInvoice[]>;
  dailyTotals: Record<IntentionStatus, number>;
} & Record<IntentionStatus, number>;

export type EmailSignature = {
  text: string | null;
  image_url: string | null;
  image_base64: string | null;
  enabled: boolean;
};

export interface ExcelColumnMapping {
  accounting_software_id?: string;
  email?: string;
  contact_name?: string;
  invoice_id?: string;
  amount?: string;
  due_date?: string;
  date_created?: string;
  org_name?: string;
  currency?: string;
}

export interface ExcelPreviewResponse {
  headers: string[];
  sample_rows: any[][];
}

export interface DSOSchema {
  dso_days: number; // Classic: (AR / credit sales) × period_days
  ar_amount?: number;
  credit_sales_amount?: number;
  days_sales?: number;
  period_days?: number;
  total_invoice_count: number;
  total_invoice_amount: number;
  weighted_invoice_age_days?: number | null;
  currency_type: string;
  calculated_date: string;
  period_start?: string | null;
  period_end?: string | null;
  customer_organization_id?: number | null;
}

export interface DSOTimeSeriesPoint {
  date: string;
  dso_days: number;
  ar_amount?: number;
  credit_sales_amount?: number;
  period_days?: number;
}

export interface DSOTimeSeriesSchema {
  time_series: DSOTimeSeriesPoint[];
  currency_type: string;
  period_start: string;
  period_end: string;
}

export interface CustomerScoreSchema {
  customer_id: number;
  customer_name: string;
  customer_organization_name: string;
  invoice_score: number;
  responsiveness_score: number;
  final_score: number;
  risk_label: "Low risk" | "Medium risk" | "High risk";
}

export interface CollectionPerformancePoint {
  period: string; // Format: "YYYY-MM" (month), "YYYY-Q1/Q2/Q3/Q4" (quarter), or "YYYY" (year)
  collected_count: number;
  collected_amount: number;
  current_count: number;
  current_amount: number;
  overdue_count: number;
  overdue_amount: number;
  total_issued_amount: number; // Total amount issued in this period
}

export interface CollectionPerformanceSchema {
  performance: CollectionPerformancePoint[];
  currency_type: string;
  period_start: string;
  period_end: string;
  grouping: string; // "month", "quarter", or "year"
  total_collected: number;
  total_current: number;
  total_overdue: number;
  total_current_amount: number;
  total_overdue_amount: number;
  total_open_amount: number;
  total_issued_amount: number; // Total amount issued across all periods
}
